import {GetterConfig} from './getterConfig'
import {DotPath} from '../getOrElse'
import {reduce, merge, isArray, camelCase, snakeCase, uniq, kebabCase} from 'lodash'
import {
  findAppropriateGetter,
  findMatchingFields,
  findAllDotPaths
} from '../util/finders'
import {setOnPath} from '../util/utils'
import {objectOr, Log} from '../index'

export type PathMap = {
  [key: string]: DotPath
}

export function mapPaths(
  obj: object,
  json: object = {},
  ignorePaths: DotPath = ''): PathMap {
    return reduce<string, PathMap>(
      findAllDotPaths(obj),
      (acc: PathMap, path: string) => {
        const matchingFields = findMatchingFields(path, json, ignorePaths)
        if (matchingFields.length) {
          acc[path] = matchingFields
        }
        return acc
      }, {})
}

export function mutateDotPathOptions(key: string): DotPath {
  return uniq([
    camelCase(key),
    snakeCase(key),
    kebabCase(key)
  ])
}

export function generateDefaultGetters(
  config: GetterConfig,
  key: string
): GetterConfig {
  const configKey = (!config[key].dotPaths) ?
    Object.assign(config[key], {
      dotPaths: mutateDotPathOptions(key)
    }) : config[key]
  return Object.assign(config, {
    [key]: configKey
  })
}

export function generateDefaultGettersForPartialConfig(
  config: GetterConfig
): GetterConfig {
  return reduce(Object.keys(config), (configCC, key): GetterConfig =>
    Object.assign(configCC, generateDefaultGetters(configCC, key)), config)
}

/**
 *
 * @param obj Object to map JSON to
 * @param json Json in the shape of what you expect OR get
 * @param ignorePaths DotPaths that are for some reason not to be used
 * @param optionalPartialConfig A partial config that may define getters/paths
 *                                      for a key that needs special treatment
 */
export function buildConfig(
  obj: any,
  json: object = {},
  ignorePaths: DotPath = '',
  optionalPartialConfig: GetterConfig = {},
  mapUntypedObjects: boolean = false
): GetterConfig {
  const map = mapPaths(obj, json, ignorePaths)
  return merge(reduce<string, GetterConfig>(
    Object.keys(map),
    (acc: GetterConfig, path: string): GetterConfig => {
      // Don't expand objects if we don't have them in
      // the defined object unless the user asks
      const getter = findAppropriateGetter(obj, path)
      if (getter !== objectOr || mapUntypedObjects) {
        acc[path] = {
          getter,
          dotPaths: map[path]
        }
      }
      return acc
    }, {}), generateDefaultGettersForPartialConfig(optionalPartialConfig))
}

export type Stomp = String[] | boolean

const shouldStomp = (stomper: Stomp, key: string = ''): boolean =>
  (isArray(stomper)) ? !!stomper.find(s => s === key) : stomper

const getDotPathPrefix = (value: string): string =>
  value.substring(0, value.lastIndexOf('.'))

export function setKeys<T extends object>(
  obj: T,
  json: object = {},
  config: GetterConfig,
  stomp: Stomp
): T {
  Object.keys(config).forEach(key => {
    const {dotPaths, getter, decorators} = config[key]
    if (!getter) {
      Log.warn(`No appropriate getter found for field ${key}, ` +
        `there was no default value ${getter}`)
      return
    }

    let dotPath
    if (Array.isArray(dotPaths)) {
      if (key.indexOf('.') > 0) {
        const dotPathPrefix = getDotPathPrefix(key)
        dotPath = dotPaths.filter(_ =>
          getDotPathPrefix(_) === dotPathPrefix)[0]
      }
      dotPath = dotPath || dotPaths.concat(key)
    }
    else dotPath = dotPaths
    try {
      const value = getter(
        json,
        dotPath,
        decorators
      )
      setOnPath(obj, key, value, shouldStomp(stomp))
    } catch (error) {
      Log.error(error)
    }
  })
  return obj
}

/**
 *
 * @param obj Your target object
 * @param config A GetterConfig that knows how to find values in the JSON
 * @param json A JSON object that is 'like' what you want your final object to be
 */
export function applyConfig<T extends object>(
  obj: T,
  config: GetterConfig,
  json: object = {},
  stomp: Stomp = false
): T {
  return setKeys<T>(obj, json, config, stomp)
}

export const forceApplyConfig = <T extends object>(
  obj: T,
  config: GetterConfig,
  json: object = {},
  stomp: String[]
): T => applyConfig(obj, config, json, false)
